import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { geoLocationsAPI, tripsAPI } from '../../lib/api';
import { messageErreur } from '../../lib/erreurs';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Aide } from '../ui/aide';
import ZonePicker from '../map/ZonePicker';

/**
 * Position apprise par téléphone (lot 051): le bureau a appelé le chauffeur et
 * dit où il est — un lieu connu, ou un point de la carte. Le point entre dans le
 * suivi du trajet avec la source « bureau », la fiabilité la plus basse: il ne
 * remplace aucun point frais et ne fait avancer aucune étape.
 */
export default function SignalerPositionDialog({ tripId, open, onOpenChange, onDone }) {
  const [mode, setMode] = useState('lieu');
  const [lieux, setLieux] = useState([]);
  const [lieuId, setLieuId] = useState('');
  const [point, setPoint] = useState({ latitude: '', longitude: '' });
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let actif = true;
    Promise.resolve()
      .then(() => geoLocationsAPI.getAll({ status: 'active', limit: 1000 }))
      .then((rows) => {
        if (actif) setLieux(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (actif) setLieux([]);
      });
    return () => {
      actif = false;
    };
  }, [open]);

  const options = lieux.map((lieu) => ({
    value: lieu.id,
    label: lieu.name,
    description: [lieu.city, lieu.country].filter(Boolean).join(', ') || undefined,
  }));
  const pret =
    mode === 'lieu'
      ? Boolean(lieuId)
      : Number.isFinite(Number(point.latitude)) &&
        Number.isFinite(Number(point.longitude)) &&
        point.latitude !== '' &&
        point.longitude !== '';

  const envoyer = async () => {
    if (!pret || envoi) return;
    setEnvoi(true);
    try {
      await tripsAPI.reportOfficePosition(
        tripId,
        mode === 'lieu'
          ? { zone_id: lieuId }
          : { latitude: Number(point.latitude), longitude: Number(point.longitude) },
      );
      toast.success('Position enregistrée');
      onOpenChange(false);
      if (onDone) onDone();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'La position n’a pas pu être enregistrée.'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1200] max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle>Signaler la position</DialogTitle>
            <Aide titre="Signaler la position">
              À utiliser après avoir appelé le chauffeur. Le point est noté « signalé par le bureau » : c’est la position
              la moins fiable, elle ne remplace pas celle du téléphone ou du boîtier et ne fait pas avancer le trajet.
            </Aide>
          </div>
          <DialogDescription className="sr-only">Choisir un lieu connu ou un point de la carte.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2" role="group" aria-label="Façon de signaler">
            <Button
              type="button"
              variant={mode === 'lieu' ? 'default' : 'outline'}
              onClick={() => setMode('lieu')}
              data-testid="position-mode-lieu"
            >
              Un lieu connu
            </Button>
            <Button
              type="button"
              variant={mode === 'carte' ? 'default' : 'outline'}
              onClick={() => setMode('carte')}
              data-testid="position-mode-carte"
            >
              Un point sur la carte
            </Button>
          </div>
          {mode === 'lieu' ? (
            <div>
              <Label>Lieu</Label>
              <SearchableSelect
                value={lieuId}
                onValueChange={setLieuId}
                options={options}
                placeholder="Choisir un lieu"
                searchPlaceholder="Rechercher un lieu…"
                emptyMessage="Aucun lieu enregistré."
              />
            </div>
          ) : (
            <ZonePicker
              latitude={point.latitude}
              longitude={point.longitude}
              radiusMeters={100}
              name="Position du camion"
              onChange={(patch) =>
                setPoint((courant) => ({
                  latitude: patch.latitude ?? courant.latitude,
                  longitude: patch.longitude ?? courant.longitude,
                }))
              }
            />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button type="button" onClick={envoyer} disabled={!pret || envoi} data-testid="position-envoyer">
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
